import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';

interface FileUploadPanelProps {
  isChecking: boolean;
  onFileSelected: (file: File) => void;
}

export function FileUploadPanel({ isChecking, onFileSelected }: FileUploadPanelProps) {
  const { t } = useTranslation('clientImport');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  function openFilePicker() {
    if (fileInputRef.current !== null) {
      fileInputRef.current.click();
    }
  }

  function handleInputChange(changeEvent: React.ChangeEvent<HTMLInputElement>) {
    const files = changeEvent.target.files;
    if (files === null) {
      return;
    }
    const selectedFile = files.item(0);
    if (selectedFile !== null) {
      onFileSelected(selectedFile);
    }
    changeEvent.target.value = '';
  }

  function handleDrop(dropEvent: React.DragEvent<HTMLDivElement>) {
    dropEvent.preventDefault();
    setIsDraggingOver(false);
    const droppedFile = dropEvent.dataTransfer.files.item(0);
    if (droppedFile !== null) {
      onFileSelected(droppedFile);
    }
  }

  return (
    <Card padding="lg">
      <h2 className="text-base font-bold text-zooindigo">{t('upload.title')}</h2>
      <p className="text-sm text-zoomuted mt-1">{t('upload.description')}</p>

      <div
        role="button"
        tabIndex={0}
        onClick={openFilePicker}
        onKeyDown={function handleKeyDown(keyboardEvent) {
          if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
            openFilePicker();
          }
        }}
        onDragOver={function handleDragOver(dragEvent) {
          dragEvent.preventDefault();
          setIsDraggingOver(true);
        }}
        onDragLeave={function handleDragLeave() {
          setIsDraggingOver(false);
        }}
        onDrop={handleDrop}
        className={cn(
          'mt-4 rounded-xl border-2 border-dashed p-8 flex flex-col items-center justify-center gap-3 text-center cursor-pointer transition-colors',
          isDraggingOver === true
            ? 'border-zooprimary bg-zoovpalelavender'
            : 'border-zooborder bg-zoooffwhite'
        )}
      >
        {isChecking === true ? (
          <>
            <LoadingSpinner className="w-6 h-6" />
            <span className="text-sm text-zoomuted">{t('upload.checking')}</span>
          </>
        ) : (
          <>
            <Icon name="Upload" className="text-zooprimary w-7 h-7" />
            <span className="text-sm text-zoomuted">{t('upload.dropzone')}</span>
            <Button
              variant="outline"
              size="small"
              onClick={function preventBubble(clickEvent) {
                clickEvent.stopPropagation();
                openFilePicker();
              }}
            >
              {t('upload.chooseFile')}
            </Button>
          </>
        )}
      </div>

      <p className="text-xs text-zoomuted mt-3">{t('upload.csvOnlyHint')}</p>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={handleInputChange}
      />
    </Card>
  );
}
